import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { articlesService } from '../../services/api';
import { uploadToCloudinary, uploadMultipleToCloudinary } from '../../utils/cloudinaryUpload';

import { FaSave, FaTimes, FaPlus, FaTrash } from 'react-icons/fa';
import Loading from '../../components/Common/Loading';

const ArticleForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    program_id: 'international-mobility',
    excerpt: '',
    content: '',
    author: '',
    image_url: '',
    image_urls: [],
    is_published: 1
  });

  useEffect(() => {
    if (id) {
      fetchArticle();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchArticle = async () => {
    try {
      const response = await articlesService.getById(id);
      let article = response.data;
      if (response.data?.success && response.data.data) {
        article = response.data.data;
      }
      
      let parsedImageUrls = [];
      if (article.image_urls) {
        if (typeof article.image_urls === 'string') {
          try {
            parsedImageUrls = JSON.parse(article.image_urls);
          } catch (e) {
            console.error('Error parsing image_urls JSON:', e);
            parsedImageUrls = [];
          }
        } else if (Array.isArray(article.image_urls)) {
          parsedImageUrls = article.image_urls;
        }
      }

      setFormData({
        title: article.title || '',
        program_id: article.program_id || 'international-mobility',
        excerpt: article.excerpt || '',
        content: article.content || '',
        author: article.author || '',
        image_url: article.image_url || '',
        image_urls: parsedImageUrls,
        is_published: article.is_published !== undefined ? Number(article.is_published) : 1
      });
    } catch (error) {
      console.error('Error fetching article:', error);
      navigate('/articles');
    } finally {
      setLoading(false);
    }
  };

  const handleMainImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploadingMain(true);
      const result = await uploadToCloudinary(file);
      if (result.success) {
        setFormData(prev => ({ ...prev, image_url: result.data.url }));
      } else {
        alert(result.message || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image. Check your Cloudinary configuration.');
    } finally {
      setUploadingMain(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingGallery(true);
    try {
      const uploadedUrls = await uploadMultipleToCloudinary(files);
      if (uploadedUrls.length > 0) {
        setFormData(prev => ({
          ...prev,
          image_urls: [...prev.image_urls, ...uploadedUrls]
        }));
      }
    } catch (error) {
      console.error('Error uploading gallery images:', error);
      alert('Error uploading images. Check your Cloudinary configuration.');
    } finally {
      setUploadingGallery(false);
    }
  };


  const handleRemoveImageUrl = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      image_urls: prev.image_urls.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        ...formData,
        is_published: Number(formData.is_published)
      };

      if (id) {
        await articlesService.update(id, payload);
      } else {
        await articlesService.create(payload);
      }
      navigate('/articles');
    } catch (error) {
      console.error('Error saving article:', error);
      alert('Error during saving');
    } finally {
      setSaving(false);
    }
  };

  const programs = [
    { value: 'international-mobility', label: 'International Mobility' },
    { value: 'global-citizenship', label: 'Global Citizenship' },
    { value: 'cooperation-development', label: 'Cooperation & Development' },
    { value: 'youth-empowerment', label: 'Youth Empowerment' }
  ];

  if (loading) return <Loading />;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {id ? 'Edit Article' : 'New Article'}
        </h1>
        <p className="mt-1 text-gray-600">Fill in the blog article details</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-semibold mb-6">General Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 mb-2 font-medium">Article Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                required
                placeholder="e.g. Erasmus+ Youth Exchange in Siedlce"
              />
            </div>

            <div>
              <label className="block text-gray-700 mb-2 font-medium">Associated Program *</label>
              <select
                value={formData.program_id}
                onChange={(e) => setFormData({...formData, program_id: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                required
              >
                {programs.map(prog => (
                  <option key={prog.value} value={prog.value}>{prog.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 mb-2 font-medium">Author *</label>
              <input
                type="text"
                value={formData.author}
                onChange={(e) => setFormData({...formData, author: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                required
                placeholder="e.g. EFIS Team"
              />
            </div>

            <div>
              <label className="block text-gray-700 mb-2 font-medium">Publication Status *</label>
              <select
                value={formData.is_published}
                onChange={(e) => setFormData({...formData, is_published: Number(e.target.value)})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                required
              >
                <option value={1}>Published</option>
                <option value={0}>Draft</option>
              </select>
            </div>

            {/* Main Image Upload */}
            <div className="md:col-span-2">
              <label className="block text-gray-700 mb-2 font-medium">Main Image *</label>
              <div className="flex items-center space-x-4">
                {formData.image_url && (
                  <div className="relative w-32 h-20 border rounded-lg overflow-hidden flex-shrink-0 bg-gray-50">
                    <img src={formData.image_url} alt="Main Preview" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image_url: '' })}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-700"
                      title="Remove image"
                    >
                      ✕
                    </button>
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleMainImageUpload}
                    className="hidden"
                    id="main-image-upload"
                  />
                  <label
                    htmlFor="main-image-upload"
                    className="cursor-pointer inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                  >
                    {uploadingMain ? 'Uploading...' : 'Choose Image'}
                  </label>
                  <p className="mt-1 text-xs text-gray-500">Allowed formats: JPG, PNG, GIF, WEBP</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-gray-700 mb-2 font-medium">Excerpt *</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
              rows="3"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
              required
              placeholder="A short summary of the article for list pages..."
            />
          </div>

          <div className="mt-6">
            <label className="block text-gray-700 mb-2 font-medium">Content *</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              rows="12"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 font-sans"
              required
              placeholder="Complete content of the article..."
            />
          </div>
        </div>

        {/* Supplementary Images Gallery */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-semibold mb-2">Supplementary Images (Gallery)</h2>
          <p className="text-sm text-gray-500 mb-6 font-normal">Add multiple images to the article gallery.</p>
          
          <div className="space-y-4">
            <div>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleGalleryUpload}
                className="hidden"
                id="gallery-images-upload"
              />
              <label
                htmlFor="gallery-images-upload"
                className="cursor-pointer inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
              >
                {uploadingGallery ? 'Uploading...' : 'Upload Images'}
              </label>
            </div>

            {formData.image_urls.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                {formData.image_urls.map((url, idx) => (
                  <div key={idx} className="relative group border border-gray-200 rounded-lg overflow-hidden h-24 bg-gray-50">
                    <img 
                      src={url} 
                      alt={`Gallery ${idx + 1}`} 
                      className="w-full h-full object-cover" 
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImageUrl(idx)}
                      className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full hover:bg-red-700 opacity-0 group-hover:opacity-100 transition shadow flex items-center justify-center"
                      title="Remove image"
                    >
                      <FaTrash size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/articles')}
            className="px-6 py-3 border rounded-lg hover:bg-gray-50 flex items-center gap-2 transition"
          >
            <FaTimes />
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 transition shadow"
          >
            <FaSave />
            {saving ? 'Saving...' : (id ? 'Update' : 'Create')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ArticleForm;
